// src/components/reviewerdash/OverdueReviews.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  Eye, 
  Calendar,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Clock,
  User,
  Mail,
  Download,
  Play,
  AlertTriangle,
  XCircle,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import RichTextViewer from '../common/RichTextViewer';
import { showToast } from '../../utils/toast';
import { handleDownloadFile } from '../../utils/utils.global';

// Enhanced parseQuillContent function to handle array format
const parseQuillContent = (content) => {
  if (!content) return '';

  try {
    // If it's already a string with HTML, return as is
    if (typeof content === 'string' && (content.includes('<p>') || content.includes('<div>'))) {
      return content;
    }

    // If it's an array of operations (like your email body format)
    if (Array.isArray(content)) {
      let html = '';
      content.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.color) {
            text = `<span style="color: ${op.attributes.color}">${text}</span>`;
          }
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      // Convert newlines to <br> tags for proper formatting
      html = html.replace(/\n/g, '<br>');

      return html;
    }

    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      return parseQuillContent(content.ops);
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return parseQuillContent(parsed);
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

const OverdueReviews = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    critical: 0,
    urgent: 0,
    avgDaysOverdue: 0,
    highReminderCount: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionDays, setExtensionDays] = useState('7');
  const [extensionReason, setExtensionReason] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch overdue reviews
  useEffect(() => {
    const fetchOverdueReviews = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/reviewer/overdue-reviews', {
          params: {
            page: currentPage,
            limit: pagination.limit,
            search: debouncedSearchTerm,
            priority: filterPriority !== 'all' ? filterPriority : undefined
          }
        });

        if (response.data.success) {
          setReviews(response.data.reviews);
          setStats(response.data.stats);
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching overdue reviews:', error);
        showToast('Failed to load overdue reviews. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchOverdueReviews();
  }, [currentPage, pagination.limit, debouncedSearchTerm, filterPriority]);

  const getOverdueColor = (days) => {
    if (days > 14) return 'bg-red-100 text-red-800 border-red-200';
    if (days > 7) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      normal: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const handleStartReview = (manuscriptId) => {
    navigate(`/reviewerdash/review/${manuscriptId}`);
  };

  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setShowDetailsModal(true);
  };

  const handleRequestExtension = async () => {
    try {
      showToast('Sending extension request...', 'info');

      // API call to request extension
      await axios.post('/reviewer/request-extension', {
        reviewId: selectedReview.id,
        manuscriptId: selectedReview.manuscriptId,
        extensionDays: parseInt(extensionDays),
        reason: extensionReason
      });

      showToast('Extension request sent successfully', 'success');

      setShowExtensionModal(false);
      setExtensionDays('7');
      setExtensionReason('');
    } catch (error) {
      console.error('Error requesting extension:', error);
      showToast('Failed to request extension. Please try again.', 'error');
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading overdue reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-red-600 flex items-center">
              <AlertCircle className="w-6 h-6 mr-3" />
              Overdue Reviews
            </h1>
            <p className="text-gray-600 mt-2">Reviews that have passed their due date and require immediate attention</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Overdue</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Critical (&gt;14 days)</p>
                  <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Urgent Priority</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.urgent}</p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg. Days Overdue</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.avgDaysOverdue}</p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Warning Banner */}
          {stats.highReminderCount > 0 && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Immediate Action Required</h3>
                  <p className="text-sm text-red-700 mt-1">
                    You have {stats.total} overdue reviews. Please prioritize these to avoid further delays in the publication process.
                    {stats.highReminderCount > 0 && 
                      ` ${stats.highReminderCount} reviews have received 3 or more reminders.`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title or manuscript ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <select
                  value={filterPriority}
                  onChange={(e) => {
                    setFilterPriority(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                </select>
              </div>
            </div>
          </div>

          {/* Overdue Reviews Table */}
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No overdue reviews</h3>
              <p className="text-gray-500">Great job! You have no overdue reviews at this time.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-red-600">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Priority
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Manuscript
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Due Date
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Days Overdue
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                        Reminders
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reviews.map((review) => (
                      <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(review.priority)}`}>
                            {review.priority.charAt(0).toUpperCase() + review.priority.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{review.manuscriptId}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">{review.title}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {review.authors?.slice(0, 2).join(', ')}
                              {review.authors?.length > 2 && ' et al.'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {review.dueDate}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getOverdueColor(review.daysOverdue)}`}>
                            {review.daysOverdue} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            <span className="font-medium">{review.reminderCount}</span> reminders
                          </div>
                          {review.lastReminder && (
                            <div className="text-xs text-gray-400">
                              Last: {review.lastReminder}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewDetails(review)}
                              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleStartReview(review.manuscriptId)}
                              className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                            >
                              <Play className="w-3 h-3" />
                              Review Now
                            </button>
                            <button
                              onClick={() => {
                                setSelectedReview(review);
                                setShowExtensionModal(true);
                              }}
                              className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Request Extension"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Details Modal */}
      {showDetailsModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Overdue Review Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Manuscript Info */}
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">{selectedReview.manuscriptId}</p>
                      <h4 className="text-lg font-semibold text-gray-900">{selectedReview.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">by {selectedReview.authors?.join(', ')}</p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(selectedReview.priority)}`}>
                      {selectedReview.priority.charAt(0).toUpperCase() + selectedReview.priority.slice(1)} Priority
                    </span>
                  </div>
                  {selectedReview.isWomenInScience && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 rounded">
                      Women in Contemporary Science in Africa
                    </span>
                  )}
                  {selectedReview.isBelispointAcademic && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      Belispoint Academic
                    </span>
                  )}
                  {selectedReview.isKidnappingForRansom && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                      Kidnapping For Ransom
                    </span>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Invited Date</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReview.invitedDate}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Due Date</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReview.dueDate}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Days Overdue</p>
                    <p className={`text-lg font-semibold ${selectedReview.daysOverdue > 14 ? 'text-red-600' : 'text-orange-600'}`}>
                      {selectedReview.daysOverdue} days
                    </p>
                  </div>
                </div>

                {/* Reminder History */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-3">Reminder History</h5>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Reminders Sent:</p>
                      <p className="text-2xl font-bold text-orange-600">{selectedReview.reminderCount}</p>
                    </div>
                    {selectedReview.lastReminder && (
                      <div>
                        <p className="text-sm text-gray-600">Last Reminder:</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedReview.lastReminder}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Abstract */}
                {selectedReview.abstract && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Abstract</h5>
                    <div className="bg-gray-50 p-4 rounded-lg prose max-w-none">
                      {/* <div dangerouslySetInnerHTML={{ 
                        __html: parseQuillContent(selectedReview.abstract) || 'No abstract available' 
                      }} /> */}
                      <RichTextViewer content={selectedReview.abstract} />
                    </div>
                  </div>
                )}

                {/* Files */}
                {selectedReview.files && Object.keys(selectedReview.files).length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Manuscript Files</h5>
                    <div className="space-y-2">
                      {Object.entries(selectedReview.files).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-700 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="ml-2 text-sm text-gray-600">{value}</span>
                          </div>
                          <button 
                            onClick={() => handleDownloadFile(value, key)}
                            className="p-1 text-gray-500 hover:text-red-600 hover:bg-white rounded transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowExtensionModal(true);
                  }}
                  className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50"
                >
                  Request Extension
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleStartReview(selectedReview.manuscriptId);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Review Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extension Request Modal */}
      {showExtensionModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Extension</h3>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Request an extension for the review of <span className="font-medium">{selectedReview.title}</span>.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Days Needed
                  </label>
                  <select 
                    value={extensionDays}
                    onChange={(e) => setExtensionDays(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="3">3 days</option>
                    <option value="5">5 days</option>
                    <option value="7">7 days</option>
                    <option value="10">10 days</option>
                    <option value="14">14 days</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Extension
                  </label>
                  <textarea
                    rows="3"
                    value={extensionReason}
                    onChange={(e) => setExtensionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Please explain why you need more time..."
                  ></textarea>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    Note: Extension requests are subject to editor approval. You will be notified via email once a decision is made.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowExtensionModal(false);
                    setExtensionDays('7');
                    setExtensionReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRequestExtension}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueReviews;